import { useState, useRef } from 'react';
import { uploadMultipleFiles, validateFileSize, formatFileSize, queueForRetry } from '../lib/storage';

const B = {
  green: '#95C11F', greenLight: '#edf7d6', greenDark: '#5a7a0a',
  blue:  '#36A9E1', blueLight:  '#d6effa', blueDark:  '#1a7aab',
  red:   '#a32d2d', redLight:   '#fcebeb',
  amber: '#ba7517', amberLight: '#faeeda',
};

/**
 * PhotoUploader
 *
 * Props:
 *   customerId  {string}   - Supabase customers.id
 *   staffName   {string}   - Name of logged-in staff member
 *   visitDate   {string}   - ISO date "2026-04-18" (defaults to today)
 *   onComplete  {function} - Called with array of saved media records on success
 *   onCancel    {function} - Called when user cancels
 *   t           {object}   - Theme tokens
 */
export default function PhotoUploader({
  customerId,
  staffName,
  visitDate,
  onComplete,
  onCancel,
  t,
}) {
  const [files,    setFiles]    = useState([]);   // { file, preview, id, error }
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [uploadErrors, setErrors] = useState([]);
  const [done,      setDone]      = useState(false);
  const fileRef = useRef(null);

  const today = visitDate || new Date().toISOString().split('T')[0];

  // Add files from picker or camera
  const addFiles = (rawFiles) => {
    const newFiles = [];
    const errors   = [];

    Array.from(rawFiles).forEach(file => {
      const { valid, error } = validateFileSize(file);
      if (!valid) {
        errors.push(error);
        return;
      }
      newFiles.push({
        id:      Math.random().toString(36).slice(2),
        file,
        preview: URL.createObjectURL(file),
        name:    file.name,
        size:    formatFileSize(file.size),
        type:    file.type,
        status:  'ready', // ready | uploading | done | error
      });
    });

    setFiles(p => [...p, ...newFiles]);
    if (errors.length) setErrors(errors);
  };

  const removeFile = (id) => {
    setFiles(p => p.filter(f => f.id !== id));
  };

  const openCamera = () => {
    fileRef.current.accept   = 'image/*,video/*';
    fileRef.current.capture  = 'environment';
    fileRef.current.multiple = false;
    fileRef.current.click();
  };

  const openPicker = () => {
    fileRef.current.accept   = 'image/*,video/*';
    fileRef.current.removeAttribute('capture');
    fileRef.current.multiple = true;
    fileRef.current.click();
  };

  const handleUpload = async () => {
    if (!files.length) return;
    if (!customerId) {
      setErrors(['No customer ID — cannot upload. Please ensure you are viewing a customer stop.']);
      return;
    }

    setUploading(true);
    setErrors([]);

    const rawFiles = files.map(f => f.file);

    const results = await uploadMultipleFiles(
      rawFiles,
      customerId,
      staffName || 'Staff',
      today,
      (pct) => setProgress(pct)
    );

    const succeeded = results.filter(r => !r.error);
    const failed    = results.filter(r =>  r.error);

    // Queue failed uploads for retry info
    failed.forEach(r => {
      queueForRetry({
        customerId,
        staffName,
        visitDate: today,
        fileName: r.file.name,
        fileSize: r.file.size,
      });
    });

    if (failed.length) {
      setErrors(failed.map(r =>
        `${r.file.name}: ${r.error?.message || 'Upload failed'}`
      ));
    }

    setUploading(false);
    setProgress(0);

    if (succeeded.length > 0) {
      setDone(true);
      // Slight delay so user sees success state before callback
      setTimeout(() => onComplete && onComplete(succeeded.map(r => r.record)), 1200);
    }
  };

  // ── SUCCESS STATE ────────────────────────────────────────────
  if (done) return (
    <div style={{ marginTop: 12, borderTop: `0.5px solid ${t.border}`, paddingTop: 14, textAlign: 'center' }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', background: B.greenLight,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, margin: '0 auto 10px' }}>✓</div>
      <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 14, color: B.green, fontFamily: 'Montserrat,sans-serif' }}>
        {files.length} file{files.length > 1 ? 's' : ''} uploaded
      </p>
      <p style={{ margin: 0, fontSize: 12, color: t.textSec, fontFamily: 'Montserrat,sans-serif' }}>
        Saved to Supabase · pending admin review
      </p>
    </div>
  );

  // ── UPLOAD PROGRESS ──────────────────────────────────────────
  if (uploading) return (
    <div style={{ marginTop: 12, borderTop: `0.5px solid ${t.border}`, paddingTop: 14 }}>
      <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13, color: t.text, fontFamily: 'Montserrat,sans-serif' }}>
        Uploading {files.length} file{files.length > 1 ? 's' : ''}…
      </p>
      <div style={{ background: t.bgSurface, borderRadius: 8, height: 8, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{
          height: '100%', borderRadius: 8, background: B.green,
          width: `${progress}%`, transition: 'width 0.3s ease'
        }}/>
      </div>
      <p style={{ margin: 0, fontSize: 12, color: t.textSec, fontFamily: 'Montserrat,sans-serif' }}>
        {progress}% — do not close the app
      </p>
    </div>
  );

  // ── MAIN FORM ────────────────────────────────────────────────
  return (
    <div style={{ marginTop: 12, borderTop: `0.5px solid ${t.border}`, paddingTop: 14 }}>
      <p style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 13, color: t.text, fontFamily: 'Montserrat,sans-serif' }}>
        Add property photos / videos
      </p>

      {/* File size guidance */}
      <p style={{ margin: '0 0 10px', fontSize: 11, color: t.textHint, fontFamily: 'Montserrat,sans-serif' }}>
        Images up to 10MB · Videos up to 100MB · Saved permanently to cloud
      </p>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={openCamera} style={{
          flex: 1, padding: '9px 0', borderRadius: 8, background: B.blue,
          color: '#fff', border: 'none', fontSize: 12, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'Montserrat,sans-serif'
        }}>📷 Camera</button>
        <button onClick={openPicker} style={{
          flex: 1, padding: '9px 0', borderRadius: 8, border: `0.5px solid ${B.blue}`,
          color: B.blue, background: 'transparent', fontSize: 12, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'Montserrat,sans-serif'
        }}>🖼 Upload</button>
        <input
          ref={fileRef} type="file" style={{ display: 'none' }}
          onChange={e => addFiles(e.target.files)}
        />
      </div>

      {/* Error messages */}
      {uploadErrors.length > 0 && (
        <div style={{ background: B.redLight, borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
          {uploadErrors.map((err, i) => (
            <p key={i} style={{ margin: '0 0 4px', fontSize: 12, color: B.red, fontFamily: 'Montserrat,sans-serif' }}>{err}</p>
          ))}
        </div>
      )}

      {/* File previews */}
      {files.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 12 }}>
          {files.map(f => (
            <div key={f.id} style={{
              position: 'relative', aspectRatio: '1', borderRadius: 8,
              overflow: 'hidden', border: `0.5px solid ${t.border}`
            }}>
              {f.type?.startsWith('video')
                ? <div style={{ width: '100%', height: '100%', background: '#222',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', color: '#fff', gap: 4 }}>
                    <span style={{ fontSize: 20 }}>▶</span>
                    <span style={{ fontSize: 9, color: '#888', fontFamily: 'Montserrat,sans-serif' }}>{f.size}</span>
                  </div>
                : <img src={f.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
              }
              {/* File size badge */}
              <div style={{
                position: 'absolute', bottom: 3, left: 3,
                background: 'rgba(0,0,0,0.6)', borderRadius: 4,
                padding: '1px 5px', fontSize: 9, color: '#fff',
                fontFamily: 'Montserrat,sans-serif'
              }}>{f.size}</div>
              {/* Remove button */}
              <button onClick={() => removeFile(f.id)} style={{
                position: 'absolute', top: 3, right: 3, width: 18, height: 18,
                borderRadius: '50%', background: 'rgba(0,0,0,0.6)',
                color: '#fff', border: 'none', fontSize: 10, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Visit date (editable in case staff are catching up) */}
      <div style={{ marginBottom: 12 }}>
        <label style={{
          display: 'block', fontSize: 10, fontWeight: 700, color: t.textSec,
          marginBottom: 4, fontFamily: 'Montserrat,sans-serif',
          textTransform: 'uppercase', letterSpacing: '0.5px'
        }}>Visit date</label>
        <input type="date" defaultValue={today} style={{
          width: '100%', padding: '8px 12px', borderRadius: 8,
          border: `0.5px solid ${t.borderStrong}`, background: t.bgInput,
          color: t.text, fontSize: 13, fontFamily: 'Montserrat,sans-serif', boxSizing: 'border-box'
        }}/>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onCancel} style={{
          flex: 1, padding: '9px 0', borderRadius: 8,
          border: `0.5px solid ${t.borderStrong}`, background: 'transparent',
          color: t.textSec, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'Montserrat,sans-serif'
        }}>Cancel</button>
        <button onClick={handleUpload} disabled={files.length === 0} style={{
          flex: 2, padding: '9px 0', borderRadius: 8,
          background: files.length === 0 ? '#aaa' : B.green,
          color: '#fff', border: 'none', fontSize: 12, fontWeight: 700,
          cursor: files.length === 0 ? 'default' : 'pointer',
          fontFamily: 'Montserrat,sans-serif'
        }}>
          Complete{files.length > 0 ? ` · upload ${files.length} file${files.length > 1 ? 's' : ''}` : ''}
        </button>
      </div>
    </div>
  );
}
